import { Feature, MiniAction, submitMiniAction } from '@otto-ec/tracking-bct';
import { trackView } from '@otto-ec/nexus-registry/pdp/tracking';
import { VariationTrackingDataProvider } from './variation-tracking-data-provider';

export const DATA_ATTRIBUTE_VISIBILITY_TRACKING = 'data-ft5-visibility-tracking';
const VISIBILITY_FEATURE_PARENT_ID = 'VisibilityFeatureParentId';

export type VisibilityTrackingOptions = {
    componentName: string;
    intersectionThreshold: number;
};

type VisibilityTrackingTarget = {
    element: HTMLElement;
    options: VisibilityTrackingOptions;
    position: number;
};

type VisibilityTrackingTargetData = Omit<VisibilityTrackingTarget, 'element'>;

export class VisibilityTracking {
    public static readonly DEFAULT_THRESHOLD = 0.75;

    private intersectionObserver?: IntersectionObserver;
    private trackedVisibleElements = new WeakSet<HTMLElement>();
    private trackedLoadedElements = new WeakSet<HTMLElement>();
    private trackingTargetsByElement = new WeakMap<HTMLElement, VisibilityTrackingTargetData>();
    private latestIntersectionByElement = new WeakMap<HTMLElement, IntersectionObserverEntry>();
    private contentObserversByElement = new Map<HTMLElement, MutationObserver>();
    private loadObserversByElement = new Map<HTMLElement, MutationObserver>();
    private hasRegisteredVisibilityParent = false;

    constructor(private provider: VariationTrackingDataProvider) {}

    public onHtmlLoaded(): void {
        const visibilityTrackingData = this.getVisibilityTrackingData();
        const hasVisibilityTrackingTargets = this.hasVisibilityTrackingTargets();

        if (!hasVisibilityTrackingTargets) {
            return;
        }

        const variationId = this.provider.getVariationTrackingData().variationId;
        this.submitMiniActionWithVariationId(variationId);

        if (visibilityTrackingData) {
            trackView({}, visibilityTrackingData);
        }

        this.observeLateLoadedTargets(trackingData => trackView({}, trackingData));
        this.registerIntersectionObserver();
    }

    public submitMiniActionWithVariationId(variationId: string): void {
        submitMiniAction([{ featureId: variationId, action: 'scroll' }]);
    }

    public getVisibilityTrackingData(): Feature[] | undefined {
        this.resetLoadObservers();

        const loadedTargets = this.getTrackingTargets().filter(target => this.hasTrackableContent(target.element));

        if (loadedTargets.length === 0) {
            return undefined;
        }

        loadedTargets.forEach(target => this.trackedLoadedElements.add(target.element));
        this.hasRegisteredVisibilityParent = true;

        return this.createLoadedTrackingData(loadedTargets);
    }

    public hasVisibilityTrackingTargets(): boolean {
        return this.findVisibilityTrackingTargets().length > 0;
    }

    public observeLateLoadedTargets(onLoaded: (trackingData: Feature[]) => void): void {
        this.resetLoadObservers();

        this.getTrackingTargets()
            .filter(target => !this.trackedLoadedElements.has(target.element))
            .forEach(target => {
                const loadObserver = new MutationObserver(() => {
                    if (this.trackedLoadedElements.has(target.element) || !this.hasTrackableContent(target.element)) {
                        return;
                    }

                    this.trackedLoadedElements.add(target.element);
                    onLoaded(this.createLoadedTrackingData([target], !this.hasRegisteredVisibilityParent));
                    this.hasRegisteredVisibilityParent = true;
                    this.disconnectLoadObserver(target.element);
                });

                loadObserver.observe(target.element, {
                    childList: true,
                    subtree: true,
                    characterData: true,
                });

                this.loadObserversByElement.set(target.element, loadObserver);
            });
    }

    public registerIntersectionObserver(): void {
        this.resetVisibilityObservers();

        const targets = this.getTrackingTargets();
        const thresholds = this.getThresholdsFromTargets(targets);

        if (thresholds.length === 0) {
            return;
        }

        this.intersectionObserver = this.createIntersectionObserver(thresholds);
        targets.forEach(target => this.intersectionObserver?.observe(target.element));
    }

    private getTrackingTargets(): VisibilityTrackingTarget[] {
        this.trackingTargetsByElement = new WeakMap<HTMLElement, VisibilityTrackingTargetData>();

        return Array.from(this.findVisibilityTrackingTargets())
            .map((element, index) => {
                const options = this.getVisibilityTrackingOptions(element);

                if (!options) {
                    return null;
                }

                const position = index + 1;
                const target = { element, options, position };

                this.trackingTargetsByElement.set(element, { options, position });

                return target;
            })
            .filter((target): target is VisibilityTrackingTarget => target !== null);
    }

    private findVisibilityTrackingTargets(): NodeListOf<HTMLElement> {
        return document.querySelectorAll(`[${DATA_ATTRIBUTE_VISIBILITY_TRACKING}]`);
    }

    private createIntersectionObserver(thresholds: Array<number>): IntersectionObserver {
        return new IntersectionObserver((entries, observer) => this.trackVisibleTargets(entries, observer), {
            threshold: thresholds,
        });
    }

    private trackVisibleTargets(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
        const trackingData: Array<MiniAction> = [];

        entries.forEach(entry => {
            const element = entry.target as HTMLElement;
            const target = this.trackingTargetsByElement.get(element);

            if (!target || this.trackedVisibleElements.has(element)) {
                return;
            }

            this.latestIntersectionByElement.set(element, entry);

            if (!this.isVisibleEnough(entry, target.options.intersectionThreshold) || !this.isVisible(element)) {
                this.disconnectContentObserver(element);
                return;
            }

            if (this.hasTrackableContent(element)) {
                trackingData.push(this.createVisibleMiniAction(target.options));
                this.markAsVisibleTracked(element, observer);
                return;
            }

            this.observeContentChangesWhileVisible(element, target.options, observer);
        });

        if (trackingData.length > 0) {
            submitMiniAction(trackingData);
        }
    }

    private observeContentChangesWhileVisible(
        element: HTMLElement,
        options: VisibilityTrackingOptions,
        observer: IntersectionObserver
    ): void {
        if (this.contentObserversByElement.has(element)) {
            return;
        }

        const contentObserver = new MutationObserver(() => {
            const latestIntersection = this.latestIntersectionByElement.get(element);

            if (
                !latestIntersection ||
                !this.isVisibleEnough(latestIntersection, options.intersectionThreshold) ||
                !this.isVisible(element) ||
                !this.hasTrackableContent(element) ||
                this.trackedVisibleElements.has(element)
            ) {
                return;
            }

            submitMiniAction([this.createVisibleMiniAction(options)]);
            this.markAsVisibleTracked(element, observer);
        });

        contentObserver.observe(element, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        this.contentObserversByElement.set(element, contentObserver);
    }

    private markAsVisibleTracked(element: HTMLElement, observer: IntersectionObserver): void {
        this.trackedVisibleElements.add(element);
        observer.unobserve(element);
        this.disconnectContentObserver(element);
        this.latestIntersectionByElement.delete(element);
        this.trackingTargetsByElement.delete(element);
    }

    private disconnectContentObserver(element: HTMLElement): void {
        const observer = this.contentObserversByElement.get(element);

        if (!observer) {
            return;
        }

        observer.disconnect();
        this.contentObserversByElement.delete(element);
    }

    private disconnectLoadObserver(element: HTMLElement): void {
        const observer = this.loadObserversByElement.get(element);

        if (!observer) {
            return;
        }

        observer.disconnect();
        this.loadObserversByElement.delete(element);
    }

    private resetVisibilityObservers(): void {
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = undefined;

        this.contentObserversByElement.forEach(observer => observer.disconnect());
        this.contentObserversByElement.clear();

        this.trackedVisibleElements = new WeakSet<HTMLElement>();
        this.latestIntersectionByElement = new WeakMap<HTMLElement, IntersectionObserverEntry>();
    }

    private resetLoadObservers(): void {
        this.loadObserversByElement.forEach(observer => observer.disconnect());
        this.loadObserversByElement.clear();
        this.trackedLoadedElements = new WeakSet<HTMLElement>();
        this.hasRegisteredVisibilityParent = false;
    }

    private hasTrackableContent(element: HTMLElement): boolean {
        return element.children.length > 0 || (element.textContent ?? '').trim().length > 0;
    }

    private isVisibleEnough(entry: IntersectionObserverEntry, intersectionThreshold: number): boolean {
        return entry.isIntersecting && entry.intersectionRatio >= intersectionThreshold;
    }

    private isVisible(element: HTMLElement): boolean {
        return element.offsetParent !== null && element.clientHeight > 0;
    }

    private getThresholdsFromTargets(targets: VisibilityTrackingTarget[]): Array<number> {
        return Array.from(new Set(targets.map(target => target.options.intersectionThreshold)));
    }

    private createVisibleMiniAction(options: VisibilityTrackingOptions): MiniAction {
        return {
            featureId: options.componentName,
            action: 'scroll',
            status: 'visible',
        };
    }

    private createLoadedTrackingData(targets: VisibilityTrackingTarget[], includeParent = true): Feature[] {
        const trackingData = targets.map(target => ({
            id: target.options.componentName,
            name: target.options.componentName,
            position: target.position,
            parentId: VISIBILITY_FEATURE_PARENT_ID,
            status: 'loaded' as const,
            labels: {},
        }));

        if (!includeParent) {
            return trackingData;
        }

        return [
            {
                id: VISIBILITY_FEATURE_PARENT_ID,
                name: 'ProductDetailPage-Components',
                status: 'loaded' as const,
                labels: {},
            },
            ...trackingData,
        ];
    }

    private getVisibilityTrackingOptions(target: HTMLElement): VisibilityTrackingOptions | null {
        const optionsJson = target.getAttribute(DATA_ATTRIBUTE_VISIBILITY_TRACKING);

        if (!optionsJson) {
            return null;
        }

        try {
            const options = JSON.parse(optionsJson) as VisibilityTrackingOptions;

            if (!options.componentName) {
                return null;
            }

            const threshold = Number(options.intersectionThreshold);

            return {
                componentName: options.componentName,
                intersectionThreshold:
                    Number.isFinite(threshold) && threshold > 0 && threshold <= 1 ? threshold : VisibilityTracking.DEFAULT_THRESHOLD,
            };
        } catch {
            return null;
        }
    }
}
